import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpDown,
  Building2,
  CircleX,
  DoorOpen,
  Droplet,
  Hammer,
  Layers,
  PaintRoller,
  Router,
  ShieldCheck,
  Trees,
  Waves,
  Zap,
} from "lucide-react";

import "../../styles/MeadowsSpecificationAccordion.css";
import { meadowsSpecificationData } from "../../data/meadowsSpecificationData";

const iconMap = {
  foundation_structure: Building2,
  plastering: Hammer,
  flooring: Layers,
  toilets: Droplet,
  plumbing_system: Waves,
  doors_windows: DoorOpen,
  paint: PaintRoller,
  electrical_fixture: Zap,
  security_network: ShieldCheck,
  communication_network: Router,
  landscaping: Trees,
  elevator: ArrowUpDown,
};

const gridVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

export default function MeadowsSpecificationAccordion() {
  const [selectedId, setSelectedId] = useState(null);
  const hasData = Boolean(meadowsSpecificationData && meadowsSpecificationData.length > 0);
  const selectedItem =
    meadowsSpecificationData.find((item) => item.id === selectedId) || null;
  const SelectedIcon = selectedItem ? iconMap[selectedItem.id] || Building2 : Building2;

  useEffect(() => {
    if (!selectedItem) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedId(null);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedItem]);

  if (!hasData) return null;

  return (
    <section
      className="specification-section meadows-spec-section py-5"
      id="serene-specifications"
    >
      <div className="meadows-spec-glow" aria-hidden="true"></div>
      <div className="container meadows-spec-container">
        <div className="meadows-spec-layout">
          <div className="meadows-spec-aside">
            <div className="meadows-spec-kicker">Specification Schedule</div>
            <h2 className="meadows-spec-title">Vessella Meadows Specifications</h2>
            <div className="meadows-spec-divider"></div>
            <p className="meadows-spec-intro">
              A curated overview of structure, finishes, and systems that shape
              every villa across the community.
            </p>
          </div>

          <motion.div
            className="meadows-spec-grid"
            variants={gridVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {meadowsSpecificationData.map((item, index) => {
              const Icon = iconMap[item.id] || Building2;

              return (
                <motion.article
                  key={item.id}
                  className="meadows-spec-card d-flex align-items-center justify-content-between"
                  variants={cardVariants}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(item.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedId(item.id);
                    }
                  }}
                >
                  <div className="meadows-spec-card-head">
                    <div className="meadows-spec-icon" aria-hidden="true">
                      <Icon size={20} />
                    </div>
                    <span className="meadows-spec-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <div className="meadows-spec-card-copy d-flex align-items-center justify-content-between flex-grow-1">
                    <h3 className="meadows-spec-card-title mb-0">{item.title}</h3>
                    <span className="meadows-spec-arrow" aria-hidden="true">
                      <ArrowRight size={18} />
                    </span>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </div>

      {selectedItem && (
        <div
          className="meadows-spec-modal-backdrop"
          role="presentation"
          onClick={() => setSelectedId(null)}
        >
          <div
            className="meadows-spec-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="meadows-spec-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="meadows-spec-modal-close"
              aria-label="Close specifications popup"
              onClick={() => setSelectedId(null)}
            >
              <CircleX size={22} />
            </button>

            <div className="meadows-spec-modal-top d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div className="meadows-spec-icon meadows-spec-modal-icon" aria-hidden="true">
                  <SelectedIcon size={20} />
                </div>
                <div>
                  <div className="meadows-spec-modal-kicker">Specification Details</div>
                  <h3 id="meadows-spec-modal-title" className="meadows-spec-modal-title">
                    {selectedItem.title}
                  </h3>
                </div>
              </div>

              <div className="meadows-spec-modal-count">
                {String(
                  meadowsSpecificationData.findIndex((item) => item.id === selectedItem.id) + 1
                ).padStart(2, "0")}
              </div>
            </div>

            <ul className="meadows-spec-list">
              {selectedItem.points.map((point, idx) => (
                <li className="meadows-spec-item" key={`${selectedItem.id}-${idx}`}>
                  <span className="meadows-spec-mark" aria-hidden="true"></span>
                  <div className="meadows-spec-item-body">
                    {point.label && (
                      <span className="meadows-spec-chip">{point.label}</span>
                    )}
                    <span className="meadows-spec-text">{point.text}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
